
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import PerformanceOptimizationsProvider from "./components/providers/PerformanceOptimizationsProvider";
import Index from "./pages/Index";
import About from "./pages/About";
import Services from "./pages/Services";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import Locations from "./pages/Locations";
import SoborgLocation from "./pages/locations/Soborg";
import CopenhagenLocation from "./pages/locations/Copenhagen";
import OnlineLocation from "./pages/locations/Online";
import GladsaxeLocation from "./pages/locations/Gladsaxe";
import LyngbyLocation from "./pages/locations/Lyngby";
import GentofeLocation from "./pages/locations/Gentofte";
import BuddingeLocation from "./pages/locations/Buddinge";
import EmdrupLocation from "./pages/locations/Emdrup";
import NordvestLocation from "./pages/locations/Nordvest";
import NorrebroLocation from "./pages/locations/Norrebro";
import VangedeLocation from "./pages/locations/Vangede";
import DyssegardLocation from "./pages/locations/Dyssegard";
import BeginnerPianoLessons from "./pages/BeginnerPianoLessons";
import ExperiencedPianoLessons from "./pages/ExperiencedPianoLessons";
import ChildrenPianoLessons from "./pages/ChildrenPianoLessons";
import AdultBeginnerPianoLessons from "./pages/AdultBeginnerPianoLessons";
import FreeTrialBooking from "./pages/FreeTrialBooking";
import { BrowserRouter, Routes, Route } from 'react-router-dom';

// Configure QueryClient with performance optimizations
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes (replacing cacheTime which is deprecated)
      refetchOnWindowFocus: false,
      retry: 1
    },
  },
});

// Export routes configuration
export const routeConfig = [
  { path: "/", element: <Index /> },
  { path: "/om", element: <About /> },
  { path: "/undervisning", element: <Services /> },
  { path: "/blog", element: <Blog /> },
  { path: "/blog/:slug", element: <BlogPost /> },
  { path: "/kontakt", element: <Contact /> },
  { path: "/lokationer", element: <Locations /> },
  
  // Location specific pages
  { path: "/klaverundervisning-soborg", element: <SoborgLocation /> },
  { path: "/klaverundervisning-kobenhavn", element: <CopenhagenLocation /> },
  { path: "/klaverundervisning-online", element: <OnlineLocation /> },
  { path: "/klaverundervisning-gladsaxe", element: <GladsaxeLocation /> },
  { path: "/klaverundervisning-lyngby", element: <LyngbyLocation /> },
  { path: "/klaverundervisning-gentofte", element: <GentofeLocation /> },
  { path: "/klaverundervisning-buddinge", element: <BuddingeLocation /> },
  { path: "/klaverundervisning-emdrup", element: <EmdrupLocation /> },
  { path: "/klaverundervisning-nordvest", element: <NordvestLocation /> },
  { path: "/klaverundervisning-norrebro", element: <NorrebroLocation /> },
  { path: "/klaverundervisning-vangede", element: <VangedeLocation /> },
  { path: "/klaverundervisning-dyssegard", element: <DyssegardLocation /> },
  
  // Specialized teaching pages
  { path: "/klaverundervisning-begyndere", element: <BeginnerPianoLessons /> },
  { path: "/klaverundervisning-ovede", element: <ExperiencedPianoLessons /> },
  { path: "/klaverundervisning-born", element: <ChildrenPianoLessons /> },
  { path: "/klaverundervisning-voksne", element: <AdultBeginnerPianoLessons /> },
  { path: "/gratis-proevetime", element: <FreeTrialBooking /> },
  // ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE
  { path: "*", element: <NotFound /> }
];

// Main App component with client-side routing
const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <BrowserRouter>
          <Toaster />
          <Sonner />
          <PerformanceOptimizationsProvider />
          <Routes>
            {routeConfig.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
